/**
 * Les nouveautes de Lybo, et la pastille bleue qui les signale.
 *
 * Idee de Nicolas, 03/09 : « lorsque nous faisons des mises a jour de LYBO, je
 * suis d'avis de rajouter une petite pastille sur les fonctionnalites pour
 * signaler que cela est nouveau. Je n'invente rien, c'est ce que j'ai sur la
 * TESLA. [...] Ensuite a l'interieur de la page on pourrait rajouter en bas de
 * page : Nouveaute, et mettre une description de ce qui change. »
 *
 * Sa raison, et c'est elle qui compte : « je pense que ce n'est pas simple pour
 * les labos de diffuser les nouveautes de l'application et donc il y a des
 * fonctionnalites importantes qui pourraient ne pas etre utilisees ».
 *
 * ─────────────────────────────────────────────────────────────────────────
 * TROIS REGLES, ET ELLES SONT LA POUR QUE LA PASTILLE GARDE UN SENS
 * ─────────────────────────────────────────────────────────────────────────
 *
 * 1. UNE NOUVEAUTE EXPIRE. Passe `JOURS_DE_VIE`, la pastille disparait meme si
 *    personne ne l'a vue. Sans cela tout finit par etre « nouveau », et une
 *    pastille permanente n'est plus lue -- c'est le meme travers que le bandeau
 *    vert qu'on a retire de la verification d'identite le 28/08.
 *
 * 2. OUVRIR LA SECTION SUFFIT A LA MARQUER VUE. On ne demande pas a la
 *    secretaire de cliquer sur une croix : le geste de travail vaut lecture.
 *
 * 3. LE TEXTE DIT CE QUI CHANGE POUR CELLE QUI TRAVAILLE, pas ce qui a change
 *    dans le code. « La verification s'affiche maintenant dans le dossier » et
 *    non « refonte du composant VerificationDossier ».
 *
 * 4. L'ETAT « DEJA VU » SUIT LE COMPTE, PAS LE POSTE. Retour de Nicolas du
 *    04/09 : « les pastilles devraient plutot etre affichees par utilisateurs,
 *    bien que les secretaires utilisent toutes le meme identifiant ». Il vit
 *    donc dans le document `users/{email}` -- une secretaire qui change
 *    d'ordinateur ne revoit plus une pastille deja eteinte. Le localStorage
 *    reste, mais comme CACHE seulement : il peint l'ecran sans attendre le
 *    reseau, et il tient tout seul quand Firestore ne repond pas.
 */

/** Combien de temps une nouveaute reste signalee. */
const JOURS_DE_VIE = 30;

const CLE_STOCKAGE = "lybo.nouveautes.vues";

/**
 * Le registre. UNE entree par changement visible, rattachee a la SECTION ou on
 * le voit -- la cle correspond a l'ecran, pas au ticket.
 *
 * Pour en ajouter une : copier un bloc, mettre la date du jour, ecrire ce que
 * la personne verra. Rien d'autre a toucher.
 */
export const NOUVEAUTES = [
  {
    id: "code-lu-web-2026-09",
    section: "secretaryBackoffice",
    date: "2026-09-04",
    version: "3.2",
    titre: "Le code du document, lisible depuis le dossier",
    description:
      "Un bouton « Code lu » apparaît dans le bloc Identité dès qu'une carte " +
      "Vitale ou une ordonnance a été scannée. Il montre le code brut du " +
      "Datamatrix, les champs tels qu'ils ont été découpés, puis ce que Lybo " +
      "en a retenu. C'est là qu'on regarde quand une identité ne colle pas.",
  },
  {
    id: "prenoms-naissance-2026-09",
    section: "secretaryBackoffice",
    date: "2026-09-04",
    version: "3.2",
    titre: "Les prénoms de naissance au complet",
    description:
      "La tablette n'affiche plus que le premier prénom, qui est celui dont " +
      "le préleveur a besoin. Les prénoms de naissance au complet vous " +
      "arrivent ici, et sur le PDF de « Derniers RDV ».",
  },
  {
    id: "provenance-dossier-2026-09",
    section: "secretaryBackoffice",
    date: "2026-09-04",
    version: "3.2",
    titre: "D'où vient le numéro de dossier",
    description:
      "Le numéro de dossier indique maintenant s'il a été lu par le lecteur " +
      "de code-barres ou saisi à la main. Un numéro tapé mérite un second " +
      "regard, un numéro lu beaucoup moins.",
  },
  {
    id: "statuts-dossier-2026-09",
    section: "secretaryBackoffice",
    date: "2026-09-04",
    version: "3.2",
    titre: "« À vérifier », et un mot pour la collègue suivante",
    description:
      "Un dossier peut maintenant être signalé « À vérifier » quand vous " +
      "voulez qu'une collègue le regarde : il apparaît en violet ici et dans " +
      "« Derniers RDV ». Le champ sous les boutons sert à lui dire pourquoi. " +
      "Et « Dossier déjà traité » se reclique pour revenir en arrière.",
  },
  {
    id: "controles-saisie-labo-2026-09",
    section: "labSettings",
    date: "2026-09-04",
    version: "3.2",
    titre: "Les garde-fous de saisie, activables par laboratoire",
    description:
      "Le contrôle de la date de naissance et celui du format de code-barres " +
      "peuvent être désactivés laboratoire par laboratoire. Ils sont actifs " +
      "par défaut, et ils préviennent sans jamais bloquer un enregistrement.",
  },
];

/**
 * Le depot distant, pose une fois par la couche applicative (Header) quand
 * elle sait QUI est connecte. Tant qu'il est absent, tout continue de
 * fonctionner sur le seul cache local : l'ecran ne depend jamais du reseau.
 */
let depotDistant = null;
const abonnes = new Set();

function prevenirLesAbonnes() {
  abonnes.forEach((f) => {
    try { f(); } catch { /* un abonne fautif n'en penalise pas un autre */ }
  });
}

/**
 * Branche le compte utilisateur.
 *
 * `lire` rend la liste des identifiants deja vus par ce compte, `ecrire` la
 * remplace. Les deux peuvent echouer sans consequence.
 */
export function brancherCompte({ lire, ecrire }) {
  depotDistant = { lire, ecrire };
  let annule = false;
  Promise.resolve()
    .then(lire)
    .then((distantes) => {
      if (annule || !Array.isArray(distantes)) return;
      // On FUSIONNE plutot que d'ecraser : ce qui a ete vu sur ce poste avant
      // la connexion compte autant que ce que le compte savait deja.
      const union = [...new Set([...lireVues(), ...distantes])];
      ecrireVuesLocales(union);
      if (union.length !== distantes.length) {
        Promise.resolve(ecrire(union)).catch(() => {});
      }
      prevenirLesAbonnes();
    })
    .catch(() => {
      // Compte illisible : on reste sur le cache local, la pastille vivra sa
      // vie sur ce poste. C'est degrade, ce n'est pas casse.
    });
  return () => { annule = true; depotDistant = null; };
}

/** S'abonner aux changements de l'etat « deja vu ». Rend de quoi se desabonner. */
export function surChangementDesVues(f) {
  abonnes.add(f);
  return () => abonnes.delete(f);
}

/** Les identifiants deja vus, tels que ce poste les connait. Ne leve jamais. */
function lireVues() {
  try {
    const brut = window.localStorage.getItem(CLE_STOCKAGE);
    const liste = brut ? JSON.parse(brut) : [];
    return Array.isArray(liste) ? liste : [];
  } catch {
    // Navigation privee, stockage plein, reglage restrictif : on se comporte
    // comme si rien n'avait ete vu. Une pastille de trop ne casse rien.
    return [];
  }
}

/** Vrai si la nouveaute est encore dans sa fenetre de vie. */
function encoreFraiche(nouveaute, aujourdhui = new Date()) {
  const posee = new Date(nouveaute.date);
  if (Number.isNaN(posee.getTime())) return false;
  const jours = (aujourdhui - posee) / 86400000;
  return jours >= 0 && jours <= JOURS_DE_VIE;
}

/**
 * Les nouveautes a signaler pour une section : fraiches ET pas encore vues.
 */
export function nouveautesDe(section, aujourdhui = new Date()) {
  const vues = lireVues();
  return NOUVEAUTES.filter(
    (n) => n.section === section && encoreFraiche(n, aujourdhui) && !vues.includes(n.id)
  );
}

/**
 * Les nouveautes d'une section, vues ou non, tant qu'elles sont fraiches.
 * C'est ce qu'affiche le bloc en bas de page : celle qui vient d'ouvrir l'ecran
 * doit pouvoir LIRE ce qui a change, meme si la pastille vient de s'eteindre.
 */
export function nouveautesAffichablesDe(section, aujourdhui = new Date()) {
  return NOUVEAUTES.filter((n) => n.section === section && encoreFraiche(n, aujourdhui));
}

/** Ecrit le cache local. Ne leve jamais. */
function ecrireVuesLocales(liste) {
  try {
    window.localStorage.setItem(CLE_STOCKAGE, JSON.stringify(liste));
  } catch {
    // Sans stockage, la pastille reapparaitra a la prochaine visite. C'est
    // genant, ce n'est pas grave, et cela ne doit surtout pas casser l'ecran.
  }
}

/**
 * Marque toutes les nouveautes d'une section comme vues.
 *
 * Le cache local est ecrit d'abord et sans attendre : la pastille doit
 * s'eteindre sous le clic, pas apres un aller-retour reseau. L'ecriture
 * distante suit, et son echec ne se voit pas -- au pire la pastille
 * reapparaitra sur un autre poste.
 */
export function marquerSectionVue(section) {
  const vues = new Set(lireVues());
  const avant = vues.size;
  NOUVEAUTES.filter((n) => n.section === section).forEach((n) => vues.add(n.id));
  if (vues.size === avant) return;
  const liste = [...vues];
  ecrireVuesLocales(liste);
  if (depotDistant) Promise.resolve(depotDistant.ecrire(liste)).catch(() => {});
  prevenirLesAbonnes();
}

export default {
  NOUVEAUTES,
  nouveautesDe,
  nouveautesAffichablesDe,
  marquerSectionVue,
  brancherCompte,
  surChangementDesVues,
  JOURS_DE_VIE,
};
